import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Fab,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  Chip
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useQuery } from 'react-query';
import { ConfigValue } from '../../../../Config';
import { useCreatorAuth } from '../../../../Hooks/creator-use-auth';

const rowsPerPage = 5;

const getEmailDomain = (email) => email?.split('@')[1] || '';

// Fetch verified Blitz users by company
const fetchUsersByCompany = async () => {
  const res = await fetch(
    `${ConfigValue.PUBLIC_REST_API_ENDPOINT}/creatorUsers/users_by_company`
  );
  const raw = await res.json();
  const filtered = {};
  Object.keys(raw).forEach((company) => {
    const users = raw[company].filter(
      (u) => u.email && !u.email.endsWith('@gmail.com')
    );
    if (users.length) filtered[company] = users;
  });
  return filtered;
};

// Fetch Culture Club contacts
const fetchContacts = async () => {
  const res = await fetch(
    'https://blitz-backend-nine.vercel.app/api/crm/contacts'
  );
  const raw = await res.json();
  return raw.filter((c) => c.email && !c.email.endsWith('@gmail.com'));
};

export default function OutgoingPitches() {
  const { creatorToken } = useCreatorAuth();
  const creatorUsername = creatorToken?.creator_user?.username;

  const [selectedIds, setSelectedIds] = useState([]);
  const [pitchesSent, setPitchesSent] = useState(0);
  const [credits, setCredits] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [rate, setRate] = useState('');
  const [ccEmail, setCcEmail] = useState('');
  const [sending, setSending] = useState(false);

  // Fetch CredX credits
  const fetchCredxMapping = async (username) => {
    try {
      const res = await fetch(
        `${ConfigValue.PUBLIC_REST_API_ENDPOINT}/credx/credits?creator_username=${username}`
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error);
      }
      const data = await res.json();
      setCredits(data.credits);
    } catch (error) {
      console.error('Error fetching credits:', error.message);
    }
  };

  useEffect(() => {
    if (creatorUsername) fetchCredxMapping(creatorUsername);
  }, [creatorUsername]);

  // Queries
  const {
    data: usersByCompany,
    isLoading: loadingUsers,
    error: errorUsers
  } = useQuery('usersByCompany', fetchUsersByCompany);
  const {
    data: contacts,
    isLoading: loadingContacts,
    error: errorContacts
  } = useQuery('contacts', fetchContacts);

  const processedUsers = usersByCompany || {};
  const processedContacts = contacts || [];

  // Top 10 domain recommendations
  const recommendations = useMemo(() => {
    const domainCounts = {};
    processedContacts.forEach((c) => {
      const d = getEmailDomain(c.email);
      if (!d) return;
      if (!domainCounts[d]) domainCounts[d] = { count: 0, contacts: [] };
      domainCounts[d].count += 1;
      domainCounts[d].contacts.push(c);
    });
    const sorted = Object.keys(domainCounts).sort(
      (a, b) => domainCounts[b].count - domainCounts[a].count
    );
    return sorted.flatMap((d) => domainCounts[d].contacts).slice(0, 10);
  }, [processedContacts]);

  const allLoaded = !loadingUsers && !loadingContacts;

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleOpenDialog = () => {
    if (credits > 0 && selectedIds.length > 0) setDialogOpen(true);
    else
      alert(
        'Not enough credits or no contacts selected. Please select at least one and ask our team for credits if needed.'
      );
  };
  const handleCloseDialog = () => setDialogOpen(false);

  const sendPitch = async () => {
    if (!ccEmail) {
      alert('Your Email (CC) is required.');
      return;
    }
    setSending(true);
    try {
      // Subtract credits
      const resCred = await fetch(
        `${ConfigValue.PUBLIC_REST_API_ENDPOINT}/credx/subtract_credits`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            creator_username: creatorUsername,
            email_count: selectedIds.length
          })
        }
      );
      if (!resCred.ok) {
        const err = await resCred.json();
        throw new Error(err.error);
      }
      const credData = await resCred.json();
      setCredits(credData.credits);
      setPitchesSent((prev) => prev + selectedIds.length);

      // Send individual pitch emails
      for (const id of selectedIds) {
        let recipients = [];
        if (processedUsers[id]) {
          recipients = processedUsers[id].map((u) => u.email);
        } else {
          const contact = processedContacts.find((c) => c.email === id);
          if (contact) recipients = [contact.email];
        }
        for (const email of recipients) {
          await fetch(
            `${ConfigValue.PUBLIC_REST_API_ENDPOINT}/credx/send_pitch_email`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                recipient_email: email,
                subject,
                body,
                cc_email: ccEmail,
                creator_username: creatorUsername
              })
            }
          );
        }
      }
      handleCloseDialog();
      setSelectedIds([]);
    } catch (error) {
      alert(`Error sending pitch: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom fontWeight={800}>
        Outbound Pitches
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography>Pitches Sent: {pitchesSent}</Typography>
        <Typography>Credits: {credits}</Typography>
      </Box>

      {credits === 0 && <Alert severity="info" sx={{ mb: 3 }}>
        If you want to pitch to brands, ask our team for credits.
      </Alert>}

      {!allLoaded && <CircularProgress sx={{ mt: 4 }} />}

      {allLoaded && (
        <>
          {/* Recommendations */}
          <Typography variant="h6" sx={{ mt: 4 }}>
            Recommendations - 1 credit each
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Company</TableCell>
                  <TableCell>Domain</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recommendations.map((c) => {
                  const id = c.email;
                  return (
                    <TableRow key={id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.includes(id)}
                          onChange={() => toggleSelect(id)}
                        />
                      </TableCell>
                      <TableCell>{c.company_name || getEmailDomain(c.email)}</TableCell>
                      <TableCell>
                        <Link href={`https://${getEmailDomain(c.email)}`} target="_blank">
                          {getEmailDomain(c.email)}
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Verified Blitz Users */}
          <Typography variant="h6" sx={{ mt: 2 }}>
            Verified Blitz Users - 2 credits each
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Company</TableCell>
                  <TableCell>Domain</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(processedUsers).map((company) => {
                  const id = company;
                  const domain = getEmailDomain(processedUsers[company][0].email);
                  return (
                    <TableRow key={company}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.includes(id)}
                          onChange={() => toggleSelect(id)}
                        />
                      </TableCell>
                      <TableCell>{company}</TableCell>
                      <TableCell>
                        <Link href={`https://${domain}`} target="_blank">
                          {domain}
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Culture Club Connections */}
          <Typography variant="h6" sx={{ mt: 2 }}>
            Culture Club Connections - 1 credit each
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 6 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Company</TableCell>
                  <TableCell>Domain</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {processedContacts.map((c) => {
                  const id = c.email;
                  return (
                    <TableRow key={id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.includes(id)}
                          onChange={() => toggleSelect(id)}
                        />
                      </TableCell>
                      <TableCell>{c.company_name || getEmailDomain(c.email)}</TableCell>
                      <TableCell>
                        <Link href={`https://${getEmailDomain(c.email)}`} target="_blank">
                          {getEmailDomain(c.email)}
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Send FAB */}
          {selectedIds.length > 0 && (
            <Fab
              color="primary"
              sx={{ position: 'fixed', bottom: 16, right: 16 }}
              onClick={handleOpenDialog}
            >
              <SendIcon />
            </Fab>
          )}

          {/* Pitch Dialog */}
          <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle>Send Pitch</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Craft your email and we’ll send it to the selected contacts.
              </DialogContentText>
              <TextField
                label="Subject"
                fullWidth
                sx={{ mb: 2 }}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
              <TextField
                label="Body"
                fullWidth
                multiline
                rows={6}
                sx={{ mb: 2 }}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
              <TextField
                label="Rate"
                fullWidth
                sx={{ mb: 2 }}
                value={rate}
                onChange={(e) => setRate(e.target.value)}
              />
              <TextField
                label="Your Email (CC)"
                fullWidth
                required
                value={ccEmail}
                onChange={(e) => setCcEmail(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button variant="contained" disabled={sending} onClick={sendPitch}>
                {sending ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Send'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}
